import { useState } from "react";
import {
  LogOut,
  User,
  Mail,
  Phone,
  Building2,
  Warehouse,
  Briefcase,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { resetFilters } from "../store/slices/filterSlice";
import { NavLink, useLocation } from "react-router-dom";
import { Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SUPER_ADMIN, VISO_SUPER_ADMIN } from "../constants";
import { selectIsWarehouse, selectCanSwitchModules } from "../store/slices/moduleSlice"; // NEW

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2 rounded ${
    isActive ? "border-l-4 border-white bg-blue-600" : "hover:bg-gray-800"
  }`;

function Sidebar() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [openProfile, setOpenProfile] = useState(false);

  const isWarehouse = useSelector(selectIsWarehouse); // NEW
  const canSwitchModules = useSelector(selectCanSwitchModules); // NEW

  const getSafeValue = (key, fallback = "") => {
    const value = localStorage.getItem(key);
    return value && value !== "null" && value !== "undefined"
      ? value
      : fallback;
  };

  const firstName = getSafeValue("first_name", "User");
  const lastName = getSafeValue("last_name");
  const companyName = getSafeValue("company_name");
  const email = getSafeValue("email");
  const phoneNumber = getSafeValue("phone_number");
  const role = getSafeValue("role_name");
  const userName = getSafeValue("user_name");
  const yard = getSafeValue("yard_name"); // this field actually holds either a yard OR warehouse name
  const companyId = getSafeValue("company_id");

  if (location.pathname === "/") return null;

  const firstLetter = firstName?.charAt(0)?.toUpperCase() || "?";

  const logout = () => {
    setOpenProfile(false);
    localStorage.clear();
    dispatch(resetFilters());
    window.location.href = "/";
  };

  const renderCompanyLink = () => {
    if (role === VISO_SUPER_ADMIN) {
      return (
        <NavLink to="/company" className={linkClass}>
          🏢 <span>Company Management</span>
        </NavLink>
      );
    }
    if (role === SUPER_ADMIN) {
      return (
        <NavLink to={`/company/${companyId}/yards`} state={{ company_name: companyName }} className={linkClass}>
          🏢 <span>Company Management</span>
        </NavLink>
      );
    }
    return null;
  };

  return (
    <>
      {/* SIDEBAR */}
      <div className="w-64 h-full bg-gray-900 text-white p-6 flex flex-col justify-between">
        <nav className="space-y-4">
          <h1 className="text-2xl font-bold mb-8">VISOTONICS</h1>

          <NavLink to="/home" className={linkClass}>
            🏠 <span>Dashboard</span>
          </NavLink>

          <NavLink to="/report" className={linkClass}>
            📊 <span>Report</span>
          </NavLink>

          {/* NEW — Shipping Line only shown when not in Warehouse mode (site-scoped users)
              or, for switchable roles, only while actively viewing the Yard tab */}
          {!isWarehouse && (
            <NavLink to="/shipping_line" className={linkClass}>
              ⚓ <span>Shipping Line</span>
            </NavLink>
          )}

          {renderCompanyLink()}
        </nav>

        <div className="space-y-4">
          <button
            onClick={() => setOpenProfile(true)}
            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-800 transition-colors mb-2 focus:outline-none"
          >
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-lg">
              {firstLetter}
            </div>
            <div className="flex-1 text-left overflow-hidden">
              <p className="text-sm font-medium truncate text-white">
                {firstName}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {role || companyName || "View Profile"}
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* PROFILE DRAWER */}
      <Drawer
        anchor="right"
        open={openProfile}
        sx={{ "& .MuiDrawer-paper": { width: 300, maxWidth: "100%" } }}
        onClose={() => setOpenProfile(false)}
      >
        <div className="h-full bg-gradient-to-b from-blue-50 to-white">
          <div className="bg-white px-6 py-5 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">Profile</h2>
            <IconButton
              onClick={() => setOpenProfile(false)}
              size="small"
              sx={{
                color: "#6b7280",
                "&:hover": { backgroundColor: "#f3f4f6" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div className="p-6">
            <div className="flex flex-col items-center mb-6">
              <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center text-4xl font-bold shadow-lg mb-3">
                {firstLetter}
              </div>
              <h3 className="font-bold text-xl text-gray-800 text-center mb-2">
                {firstName} {lastName}
              </h3>
              {companyName && (
                <div className="flex items-center gap-2">
                  <Building2 size={16} className="text-blue-600" />
                  <p className="text-sm text-gray-600">{companyName}</p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              {email ? (
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-100 p-2.5 rounded-lg">
                      <Mail size={20} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-1 font-medium">
                        Email
                      </p>
                      <p className="text-sm text-gray-800 font-medium truncate">
                        {email}
                      </p>
                    </div>
                  </div>
                </div>
              ) : phoneNumber ? (
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-4">
                    <div className="bg-green-100 p-2.5 rounded-lg">
                      <Phone size={20} className="text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1 font-medium">
                        Phone
                      </p>
                      <p className="text-sm text-gray-800 font-medium">
                        {phoneNumber}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="bg-purple-100 p-2.5 rounded-lg">
                    <User size={20} className="text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1 font-medium">
                      User Name
                    </p>
                    <p className="text-sm text-gray-800 font-medium">
                      {userName}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="bg-orange-100 p-2.5 rounded-lg">
                    <Briefcase size={20} className="text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1 font-medium">
                      Role
                    </p>
                    <p className="text-sm text-gray-800 font-medium">{role}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 p-2.5 rounded-lg">
                    <Warehouse size={20} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    {/* FIXED — label now reflects whether this is actually a yard or warehouse */}
                    <p className="text-xs text-gray-500 mb-1 font-medium">
                      {isWarehouse ? "Warehouse" : "Yard"}
                    </p>
                    <p className="text-sm text-gray-800 font-medium">{yard}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={logout}
                  className="w-full flex items-center justify-center gap-3 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white rounded-xl p-4 transition-all shadow-sm hover:border-red-600"
                >
                  <LogOut size={20} />
                  <span className="font-semibold">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
}

export default Sidebar;